import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Truck, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from './useAuth';
import { brandLockupClass } from '@/app/AppShell';

/**
 * Sign-in against the shared new-auth-server. One login covers inventory and
 * this app; there is no sign-up, no password reset here — user administration
 * lives with the auth server's own `add-user.js`.
 */
export function LoginPage() {
  const { login, user, isLoading: authLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('redirect') || '/';

  // Already signed in (or a token that /me is still validating): never show the
  // form to someone who has a session.
  if (user) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    const formData = new FormData(event.currentTarget);
    try {
      await login(String(formData.get('username') || ''), String(formData.get('password') || ''));
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const busy = submitting || authLoading;

  return (
    <div className="grid min-h-dvh">
      <div className="flex items-center justify-center bg-background p-10 max-md:p-6 [&_button]:min-h-11 [&_input]:min-h-11"><div className="w-full max-w-sm">
        <div className="mb-8">
          <div className={`${brandLockupClass} mb-6 p-0`}><Truck aria-hidden="true" className="size-[26px] text-brand-cherry" /><span>ACOT<span className="ml-[.28em]">Orders</span></span></div>
          <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
        </div>
        <Card className="border-0 bg-transparent p-0 shadow-none">
          <CardHeader className="sr-only">
            <CardTitle className="text-center text-lg">Sign in</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
              <div className="grid gap-2">
                <Label htmlFor="username">Username</Label>
                <Input id="username" name="username" autoComplete="username" required disabled={busy} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  disabled={busy}
                />
              </div>
              {error && <p role="alert" className="rounded-md border border-danger-line bg-danger-surface p-3 text-sm text-danger-ink">{error}</p>}
              <Button type="submit" className="w-full" disabled={busy}>
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? 'Signing in…' : 'Sign in'}{!submitting && <ArrowRight />}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div></div>
    </div>
  );
}
